import React, { useState } from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(BACKEND_URL + '/api/contact/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        setError(data.detail || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('Failed to send message. Please try again or call us on 0208 153 0033.');
    }
    
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-24">
        {/* Contact Info & Form */}
        <section className="py-12 lg:py-16 bg-background">
          <div className="section-container">
            <div className="grid lg:grid-cols-2 gap-12">
              
              {/* Contact Information */}
              <div>
                <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-8">
                  Get in Touch
                </h1>
                
                <div className="space-y-6 mb-10">
                  <Card className="border-border">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground mb-1">Phone</h3>
                        <a href="tel:02081530033" className="text-accent hover:underline text-lg font-semibold">
                          0208 153 0033
                        </a>
                        <p className="text-sm text-muted-foreground mt-1">
                          Monday - Friday, 9am - 6pm
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-border">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-gold" />
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground mb-1">Email</h3>
                        <a href="mailto:contactus@bluelionclaims.co.uk" className="text-accent hover:underline">
                          contactus@bluelionclaims.co.uk
                        </a>
                        <p className="text-sm text-muted-foreground mt-1">
                          We'll respond within 24 hours
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-border">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground mb-1">Office Address</h3>
                        <p className="text-muted-foreground">
                          The Centro Buildings<br />
                          20-23 Mandela Street<br />
                          London, NW1 0DU
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-border">
                    <CardContent className="p-6 flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-gold" />
                      </div>
                      <div>
                        <h3 className="font-bold text-foreground mb-1">Opening Hours</h3>
                        <p className="text-muted-foreground">
                          Monday - Friday: 9:00am - 6:00pm<br />
                          Saturday - Sunday: Closed
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <Card className="border-border">
                  <CardContent className="p-8">
                    {isSubmitted ? (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-6">
                          <CheckCircle className="w-8 h-8 text-success" />
                        </div>
                        <h3 className="font-heading text-2xl font-bold text-foreground mb-4">
                          Message Sent
                        </h3>
                        <p className="text-muted-foreground mb-6">
                          Thank you for contacting us. A member of our team will get back to you shortly.
                        </p>
                        <Button variant="outline" onClick={() => setIsSubmitted(false)}>
                          Send Another Message
                        </Button>
                      </div>
                    ) : (
                      <>
                        <h3 className="font-heading text-xl font-bold text-foreground mb-2">
                          Send Us a Message
                        </h3>
                        <p className="text-muted-foreground mb-6">
                          Fill out the form below and we'll get back to you as soon as possible.
                        </p>

                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                              Full Name *
                            </label>
                            <Input
                              type="text"
                              value={formData.name}
                              onChange={(e) => setFormData({...formData, name: e.target.value})}
                              placeholder="Enter your full name"
                              required
                              data-testid="contact-name"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                              Email Address *
                            </label>
                            <Input
                              type="email"
                              value={formData.email}
                              onChange={(e) => setFormData({...formData, email: e.target.value})}
                              placeholder="Enter your email"
                              required
                              data-testid="contact-email"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                              Phone Number
                            </label>
                            <Input
                              type="tel"
                              value={formData.phone}
                              onChange={(e) => setFormData({...formData, phone: e.target.value})}
                              placeholder="Enter your phone number"
                              data-testid="contact-phone"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                              Message *
                            </label>
                            <textarea
                              value={formData.message}
                              onChange={(e) => setFormData({...formData, message: e.target.value})}
                              placeholder="How can we help you?"
                              required
                              rows={5}
                              className="w-full px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50"
                              data-testid="contact-message"
                            />
                          </div>

                          {error && (
                            <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                              <p className="text-sm text-red-600">{error}</p>
                            </div>
                          )}

                          <Button 
                            type="submit" 
                            variant="gold" 
                            size="lg" 
                            className="w-full" 
                            disabled={isSubmitting}
                            data-testid="contact-submit"
                          >
                            {isSubmitting ? 'Sending...' : 'Send Message'}
                            <Send className="w-5 h-5 ml-2" />
                          </Button>
                        </form>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary-foreground mb-6">
                Ready to Start Your Claim?
              </h2>
              <p className="text-lg text-primary-foreground/80 mb-8">
                Check if you may be eligible for compensation.
              </p>
              <Button variant="gold" size="lg" asChild>
                <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer">
                  Find Your Agreements
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
